import { useChangePassword } from '@myrentalmate/api-client';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import {
  FormFields,
  optional,
  required,
  useFormValues,
  type Field,
} from '../../components/FormFields';
import { apiErrorMessage } from '../../lib/apiError';
import type { Me } from '../../lib/me';

const NEW_FIELDS: Field[] = [
  {
    name: 'new_password',
    label: 'New Password',
    type: 'password',
    required: true,
    helperText: 'At least 12 characters. A short phrase works well',
  },
  { name: 'confirm', label: 'New Password Again', type: 'password', required: true },
];
const EMPTY = { current_password: '', new_password: '', confirm: '' };

export function PasswordSection({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const fields: Field[] = [
    ...(me.has_password
      ? [
          {
            name: 'current_password',
            label: 'Current Password',
            type: 'password' as const,
            required: true,
          },
        ]
      : []),
    ...NEW_FIELDS,
  ];
  const form = useFormValues(fields, EMPTY);
  const [mismatch, setMismatch] = useState(false);
  const change = useChangePassword({
    mutation: {
      onSuccess: () => {
        form.setValues(EMPTY);
        return queryClient.invalidateQueries();
      },
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const differs = form.values.new_password !== form.values.confirm;
    setMismatch(differs);
    if (differs) return;
    change.mutate({
      data: {
        current_password: optional(form.values, 'current_password'),
        new_password: required(form.values, 'new_password'),
      },
    });
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box component="form" onSubmit={submit}>
        <Typography variant="h6">Password</Typography>
        <Typography color="text.secondary">
          {me.has_password
            ? 'Changing it signs you out everywhere else.'
            : "You don't have a password yet, and sign in with emailed links. You can add one here."}
        </Typography>
        <Stack spacing={2} sx={{ alignItems: 'flex-start', mt: 1 }}>
          <Box sx={{ width: '100%' }}>
            <FormFields
              fields={fields}
              values={form.values}
              errors={form.errors}
              onChange={form.onChange}
            />
          </Box>
          {mismatch && <Alert severity="error">Those two new passwords don't match.</Alert>}
          {change.isError && <Alert severity="error">{apiErrorMessage(change.error)}</Alert>}
          {change.isSuccess && <Alert severity="success">Your password was changed.</Alert>}
          <Button type="submit" variant="outlined" disabled={change.isPending}>
            {change.isPending ? 'Saving…' : me.has_password ? 'Change password' : 'Set password'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
